import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { RotateCcw, TriangleAlert } from "lucide-react"

import { DataState } from "@/components/DataState"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { useAuth } from "@/lib/auth"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import {
  borrarPlantilla,
  fetchPlantillas,
  guardarPlantilla,
  previsualizarPlantilla,
  type IdiomaDelCorreo,
  type PlantillaDeCobranza,
  type TipoDeToque,
} from "@/routes/cobranza/api"

const IDIOMAS: Array<{ id: IdiomaDelCorreo; label: string }> = [
  { id: "es", label: "Español" },
  { id: "pt", label: "Português" },
  { id: "en", label: "English" },
]

/**
 * ARMAR EL TEXTO DE LOS AVISOS DE COBRANZA.
 *
 * Punto 13b de Cesar: «deberíamos de tener una parte para armar ese template».
 *
 * ---------------------------------------------------------------------------
 * EL IDIOMA ES EL DEL DESTINATARIO, NO EL TUYO
 *
 * Quien lee este correo es **el cliente DEL cliente**: una contadora que opera
 * Marty en español le reclama en portugués a los clientes de la sociedad
 * brasileña. Confundirlo con el idioma de la interfaz manda el correo en el
 * idioma equivocado sin que nada falle, así que el selector va arriba y dice
 * de quién es.
 *
 * ---------------------------------------------------------------------------
 * LA VISTA PREVIA NO ES UN ADORNO
 *
 * Un texto de cobranza se escribe mirando el RESULTADO: `{importe}` y
 * «PEN 1.234,56» ocupan y se leen muy distinto, y un saludo que quedaba bien
 * en el editor puede salir partido. Y sobre todo: es donde aparece el error
 * antes de guardar. Un `{descuento}` que nadie sustituye no falla al guardar,
 * falla al enviar — tres semanas después, en la gestión que importaba.
 *
 * ---------------------------------------------------------------------------
 * LO QUE NO SE TOCA SIGUE SIENDO DE MARTY, Y ESO ES UNA VENTAJA
 *
 * Las plantillas de fábrica no se copian a la tabla al crear el cliente: se
 * usan cuando no hay una propia. Así, mejorar la redacción llega a todos los
 * que no la hayan pisado. Se dice en el pie, porque cambia la decisión de
 * tocar o no tocar.
 */
export function PlantillasDialog({
  open, onOpenChange, puedeEditar,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  puedeEditar: boolean
}) {
  const { activeTenantId } = useAuth()
  const [idioma, setIdioma] = React.useState<IdiomaDelCorreo>("es")

  const q = useQuery({
    queryKey: ["cobranza-plantillas", activeTenantId, idioma],
    queryFn: () => fetchPlantillas(idioma),
    enabled: Boolean(activeTenantId) && open,
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("El texto de los avisos")}</DialogTitle>
          <DialogDescription>
            {t("Los cinco toques de la cadencia. Marty rellena los datos entre llaves al enviar.")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Label htmlFor="pl-idioma" className="text-2xs">
            {t("Idioma de tu cliente")}
          </Label>
          <div className="flex items-center rounded-md border bg-surface p-0.5">
            {IDIOMAS.map((i) => (
              <button
                key={i.id}
                type="button"
                onClick={() => setIdioma(i.id)}
                aria-pressed={idioma === i.id}
                className={cn(
                  "h-6 rounded-sm px-2 text-2xs font-medium transition-colors",
                  idioma === i.id
                    ? "bg-surface-sunken text-foreground shadow-xs"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {i.label}
              </button>
            ))}
          </div>
          <span className="text-2xs text-muted-foreground">
            {t("El de quien recibe el correo, no el tuyo.")}
          </span>
        </div>

        {q.data?.marcadores ? (
          <div className="rounded-md border bg-surface-sunken/50 px-2.5 py-2">
            <p className="text-2xs font-medium">{t("Datos que puedes usar")}</p>
            <ul className="mt-1 grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
              {Object.entries(q.data.marcadores).map(([clave, ayuda]) => (
                <li key={clave} className="text-2xs leading-4 text-muted-foreground">
                  <code className="rounded bg-surface px-1 text-foreground">
                    {"{" + clave + "}"}
                  </code>{" "}
                  {ayuda}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <DataState
          loading={q.isLoading}
          error={q.error}
          empty={!q.isLoading && !(q.data?.plantillas ?? []).length}
          onRetry={() => void q.refetch()}
          rows={5}
        >
          <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
            {(q.data?.plantillas ?? []).map((p) => (
              <EditorDePlantilla
                key={p.tipo}
                plantilla={p}
                idioma={idioma}
                puedeEditar={puedeEditar}
              />
            ))}
          </div>
        </DataState>

        {q.data?.nota ? (
          <p className="marty-nota text-2xs leading-4">{q.data.nota}</p>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("Cerrar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EditorDePlantilla({
  plantilla, idioma, puedeEditar,
}: {
  plantilla: PlantillaDeCobranza
  idioma: IdiomaDelCorreo
  puedeEditar: boolean
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [abierto, setAbierto] = React.useState(false)
  const [asunto, setAsunto] = React.useState(plantilla.asunto)
  const [cuerpo, setCuerpo] = React.useState(plantilla.cuerpo)

  React.useEffect(() => {
    setAsunto(plantilla.asunto)
    setCuerpo(plantilla.cuerpo)
  }, [plantilla.asunto, plantilla.cuerpo])

  const cambiado = asunto !== plantilla.asunto || cuerpo !== plantilla.cuerpo

  /*
    LA PREVIA SE PIDE AL SERVIDOR, y no se arma en el navegador.

    Es el MISMO código que rellena el correo de verdad (`mensaje.redactar`), y
    ésa es la razón entera: una previa hecha con un `replace` del navegador
    diría que el texto está bien y el envío fallaría igual. Una previa que
    puede discrepar del envío es peor que no tener previa.
  */
  const previa = useQuery({
    queryKey: ["cobranza-previa", plantilla.tipo, idioma, asunto, cuerpo],
    queryFn: () => previsualizarPlantilla({ tipo: plantilla.tipo, idioma, asunto, cuerpo }),
    enabled: abierto && Boolean(asunto) && Boolean(cuerpo),
  })

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ["cobranza-plantillas"] })
  }

  const guardar = useMutation({
    mutationFn: () => guardarPlantilla({ tipo: plantilla.tipo, idioma, asunto, cuerpo }),
    onSuccess: (r) => {
      invalidar()
      toast({ title: t("Plantilla guardada"), description: r.mensaje })
    },
    onError: onWriteError("la plantilla", "guardar"),
  })

  const volver = useMutation({
    mutationFn: () => borrarPlantilla(plantilla.tipo, idioma),
    onSuccess: () => {
      invalidar()
      setAsunto(plantilla.asunto_de_marty)
      setCuerpo(plantilla.cuerpo_de_marty)
      toast({ title: t("Vuelve al texto de Marty") })
    },
    onError: onWriteError("la plantilla", "restaurar"),
  })

  return (
    <section className="rounded-md border">
      <header className="flex flex-wrap items-center gap-2 border-b bg-surface-sunken/60 px-2.5 py-1.5">
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          className="text-xs font-medium"
          aria-expanded={abierto}
        >
          {etiqueta(plantilla.tipo)}
        </button>
        {plantilla.propia ? (
          <Badge variant="outline" className="h-4 px-1 text-[10px]">{t("tuya")}</Badge>
        ) : null}
        <span className="min-w-0 flex-1 truncate text-2xs text-muted-foreground">
          {plantilla.cuando}
        </span>
        {plantilla.propia && puedeEditar ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6"
            onClick={() => volver.mutate()}
            disabled={volver.isPending}
          >
            <RotateCcw className="mr-1 h-3 w-3" aria-hidden="true" />
            {t("Texto de Marty")}
          </Button>
        ) : null}
      </header>

      {abierto ? (
        <div className="space-y-2 p-2.5">
          <div className="space-y-1">
            <Label htmlFor={`as-${plantilla.tipo}`} className="text-2xs">
              {t("Asunto")}
            </Label>
            <Input
              id={`as-${plantilla.tipo}`}
              value={asunto}
              onChange={(e) => setAsunto(e.target.value)}
              disabled={!puedeEditar}
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`cu-${plantilla.tipo}`} className="text-2xs">
              {t("Cuerpo")}
            </Label>
            <Textarea
              id={`cu-${plantilla.tipo}`}
              rows={7}
              value={cuerpo}
              onChange={(e) => setCuerpo(e.target.value)}
              disabled={!puedeEditar}
              className="font-mono text-xs"
            />
          </div>

          {/*
            LOS PROBLEMAS, ANTES DEL BOTÓN Y NO DESPUÉS DE GUARDAR. Es todo el
            valor de la previa: el error aparece delante de quien lo puede
            corregir, y no tres semanas después en el log de un worker.
          */}
          {(previa.data?.problemas ?? []).map((p, i) => (
            <p key={i} className="flex gap-1.5 text-2xs leading-4 text-destructive">
              <TriangleAlert className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
              {p}
            </p>
          ))}

          {previa.data && !previa.data.problemas.length ? (
            <div className="rounded-md border bg-surface-sunken/50 p-2">
              <p className="text-2xs font-medium text-muted-foreground">
                {t("Así le llega a tu cliente")}
              </p>
              <p className="mt-1 text-xs font-medium">{previa.data.asunto}</p>
              <pre className="mt-1 whitespace-pre-wrap font-sans text-2xs leading-4">
                {previa.data.cuerpo}
              </pre>
            </div>
          ) : null}

          {puedeEditar ? (
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setAsunto(plantilla.asunto)
                  setCuerpo(plantilla.cuerpo)
                }}
                disabled={!cambiado}
              >
                {t("Descartar cambios")}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => guardar.mutate()}
                disabled={!cambiado || guardar.isPending
                          || previa.data?.se_puede_guardar === false}
              >
                {t("Guardar")}
              </Button>
            </div>
          ) : (
            <p className="text-2xs text-muted-foreground">
              {t("Tu rol no incluye editar los textos de cobranza.")}
            </p>
          )}
        </div>
      ) : null}
    </section>
  )
}

function etiqueta(tipo: TipoDeToque): string {
  switch (tipo) {
    case "antes": return t("Aviso previo")
    case "vispera": return t("La víspera")
    case "vencimiento": return t("El día del vencimiento")
    case "seguimiento": return t("Seguimiento de una vencida")
    case "escalamiento": return t("Escalamiento")
    default: return tipo
  }
}
